import React from "react";
import { ArrowRight, Phone } from "lucide-react";
import { COMPANY } from "@/data/site";

const HERO_IMG = "/projects/staircase-led.jpg";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Illuminated glass staircase installation by Hasan Javed"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 30%" }}
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-40 pb-20 md:pt-52 md:pb-32 min-h-screen flex flex-col justify-end">
        <div className="max-w-4xl fade-up">
          <div className="overline text-white/85 mb-6" data-testid="hero-overline">
            <span className="inline-block h-px w-10 bg-blue-400 align-middle mr-4" />
            Established in Dubai · Serving All Emirates
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-light text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tighter text-balance"
          >
            Premium Glass &<br />
            <span className="italic font-extralight text-white/90">Aluminum Solutions</span>
            <br />in the UAE
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-2xl text-lg md:text-xl text-white/80 font-light leading-relaxed"
          >
            Delivering excellence in glass, aluminum, mirrors and custom architectural solutions for
            residential, commercial and industrial projects.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4" data-testid="hero-ctas">
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollTo("#contact")}
              className="group bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold flex items-center gap-3 transition-colors"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-contact-btn"
              onClick={() => scrollTo("#contact")}
              className="bg-white/10 backdrop-blur-md border border-white/50 hover:bg-white/20 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
            >
              Contact Us
            </button>
            <button
              data-testid="hero-projects-btn"
              onClick={() => scrollTo("#portfolio")}
              className="text-white hover:text-blue-200 px-2 py-4 text-xs uppercase tracking-[0.22em] font-semibold flex items-center gap-2 border-b border-white/40 hover:border-white transition-colors"
            >
              View Our Projects
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-16 md:mt-24 border-t border-white/20 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-white/85">
          {[
            { k: "500+", v: "Projects Delivered" },
            { k: "7", v: "Emirates Served" },
            { k: "24/7", v: "Emergency Response" },
            { k: "100%", v: "Warranty-Backed" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display text-3xl md:text-4xl font-light tracking-tight">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
